// Aktivitäts-Feed der Lead-Seite: schlanke Filterleiste (Alle, Anrufe, Custom Activities, Notizen, E-Mails, SMS,
// Status), Zeitleiste mit Typ-Symbolen. Kurze Ereignisse (Status, erledigte Follow-ups, Anrufe ohne Notiz) als eine
// Zeile, Inhalte (Notiz, Custom Activity, Mail, Anruf mit Notiz) als ruhige Karte; lange Inhalte eingeklappt.
// Bearbeiten von Notizen und CRM-Custom-Activities direkt im Eintrag.
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { aktivitaetenApi, ApiFehler, type Aktivitaet, type AktivitaetTyp, type FeldWert, type Kontakt } from "../../api";
import { leadTiefeApi } from "../../api/leadTiefe";
import { smsEingangApi, type SmsZeile } from "../../api/smsEingang";
import { feedAusCache, feedMerken } from "../../daten/leadCache";
import { useMeta } from "../../daten/meta";
import { DISPOSITION, T } from "../../texte";
import { useTextbausteine } from "../../textbausteine/useTextbausteine";
import { customWert, feldText, istLeer, type FeldDef } from "../../werkzeuge/felder";
import { sicheresHtml, textZuHtml } from "../../werkzeuge/html";
import { loeschFristStarten } from "../../werkzeuge/loeschFrist";
import { modTaste } from "../../werkzeuge/tasten";
import { berlinDatum, dauerText, relativ, zeitpunktLang } from "../../werkzeuge/zeit";
import { AufnahmePlayer } from "../AufnahmePlayer";
import { Auswahl } from "../Auswahl";
import { Avatar, Laden, StatusPille } from "../Bausteine";
import { FehlerBox } from "../Fehler";
import { Icon, type IconName } from "../Icon";
import { useMeldung } from "../Meldungen";
import { telefonText, useAnrufen } from "../Telefonnummer";
import { mailTitel, mailZusatz, MailEntwuerfeImFeed, MailInhalt, ohneDoppelteThreads } from "../../kommunikation/mailFeed";
import { CustomActivityFormular, sendetNachAussen, werteFuerApi, type Werte } from "./CustomActivityFormular";
import { TypName } from "./CustomActivityMenue";
import { KontaktFilter, NutzerFilter, SucheFeld, ZeitFilter, type ZeitFilterWert } from "./FeedFilter";
import { kurzeZeit, tagKey, tagLabel } from "./feedHilfen";
import { TL } from "./leadTexte";
import { markerZuMark } from "./notizHtml";
import { LogReiter, type LogFilter } from "./LogReiter";
import { NotizEditor } from "./NotizEditor";
import { AutoSpeicher, type SpeicherStand } from "../../werkzeuge/autospeichern";
import { treffer, trefferLoeschen } from "./trefferMarkieren";
import "../../styles/lead.css";
import "./leadTiefe.css";

// ---------------------------------------------------------------------------
// Filter und Daten
// ---------------------------------------------------------------------------

export type FeedArt = "alle" | "anrufe" | "notizen" | "emails" | "sms" | "custom" | "status" | "log";

export interface FeedFilter extends ZeitFilterWert {
  art: FeedArt;
  typId?: string | null;
  /** Suchtext (Runde 7B): alle Wörter müssen vorkommen */
  q?: string;
  userId?: string | null;
  contactId?: string | null;
}

/** Ohne Suche, Nutzer, Kontakt und Zeitraum? Nur dann gilt der Zwischenspeicher der Lead-Seite. */
const ohneZusatz = (f: FeedFilter) => !f.q && !f.userId && !f.contactId && !f.von && !f.bis;

const TYPEN_JE_ART: Record<FeedArt, AktivitaetTyp[] | undefined> = {
  alle: undefined,
  anrufe: ["Call"],
  notizen: ["Note"],
  emails: ["Email", "EmailThread"],
  sms: ["SMS", "WhatsAppMessage"],
  custom: ["CustomActivity"],
  status: ["LeadStatusChange", "OpportunityStatusChange"],
  log: undefined,
};

/** So lange lässt sich ein Löschen rückgängig machen, bevor es wirklich ausgeführt wird. */
const LOESCH_FRIST_MS = 7000;
const NOTIZ_LOESCH_FRIST_MS = 5000;

const ARTEN: { art: FeedArt; label: string; kurz?: string }[] = [
  { art: "alle", label: T.feed.alle },
  { art: "anrufe", label: T.feed.anrufe },
  { art: "custom", label: T.feed.custom, kurz: "Custom" },
  { art: "notizen", label: T.feed.notizen },
  { art: "emails", label: T.feed.emails },
  { art: "sms", label: T.feed.sms },
  { art: "status", label: "Status" },
  { art: "log", label: TL.log.reiter },
];

function passt(a: Aktivitaet, f: FeedFilter): boolean {
  const typen = TYPEN_JE_ART[f.art];
  if (typen && !typen.includes(a._type)) return false;
  if (f.art === "custom" && f.typId && a.custom_activity_type_id !== f.typId) return false;
  if (f.q) return false; // ob ein neuer Eintrag zur Suche passt, entscheidet der Server (die Liste lädt dann neu)
  if (f.userId && a.user_id !== f.userId) return false;
  if (f.contactId && a.contact_id !== f.contactId) return false;
  if (f.von || f.bis) {
    const z = zeitVon(a);
    const tag = z ? berlinDatum(z) : "";
    if ((f.von && tag < f.von) || (f.bis && tag > f.bis)) return false;
  }
  return true;
}

function zeitVon(a: Aktivitaet): string {
  return a.activity_at ?? a.date_created ?? "";
}

function einsortieren(liste: Aktivitaet[], a: Aktivitaet): Aktivitaet[] {
  const ohne = liste.filter((x) => x.id !== a.id);
  const t = zeitVon(a);
  const i = ohne.findIndex((x) => zeitVon(x) < t);
  return i < 0 ? [...ohne, a] : [...ohne.slice(0, i), a, ...ohne.slice(i)];
}

export interface FeedDaten {
  eintraege: Aktivitaet[];
  /** angeheftete Notizen (stehen oben im Verlauf, Design-Runde 2) */
  angeheftet: Aktivitaet[];
  anheften: (a: Aktivitaet, an: boolean) => Promise<void>;
  hatMehr: boolean;
  laedt: boolean;
  laedtMehr: boolean;
  fehler: unknown;
  mehr: () => void;
  neu: () => void;
  einfuegen: (a: Aktivitaet) => void;
  entfernen: (id: string) => void;
  aktualisieren: (id: string) => void;
}

export function useFeed(leadId: string, filter: FeedFilter): FeedDaten {
  const standard = filter.art === "alle" && ohneZusatz(filter);
  const start = standard ? feedAusCache(leadId) : null;
  const [eintraege, setEintraege] = useState<Aktivitaet[]>(start?.data ?? []);
  const [angeheftet, setAngeheftet] = useState<Aktivitaet[]>(start?.angeheftet ?? []);
  const [cursor, setCursor] = useState<string | null>(start?.cursor ?? null);
  const [hatMehr, setHatMehr] = useState(start?.has_more ?? false);
  const [laedt, setLaedt] = useState(!start);
  const [laedtMehr, setLaedtMehr] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const generation = useRef(0);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const parameter = useCallback(() => {
    const f = filterRef.current;
    return {
      types: TYPEN_JE_ART[f.art],
      custom_activity_type_id: f.art === "custom" && f.typId ? f.typId : undefined,
      q: f.q || undefined,
      user_id: f.userId || undefined,
      contact_id: f.contactId || undefined,
      von: f.von || undefined,
      bis: f.bis || undefined,
      limit: 50,
    };
  }, []);

  const laden = useCallback(
    async (zusammenfuehren: boolean) => {
      const gen = ++generation.current;
      setFehler(null);
      if (filterRef.current.art === "log") {
        setLaedt(false); // der Log-Reiter lädt selbst
        return;
      }
      try {
        const r = await leadTiefeApi.verlauf(leadId, parameter());
        if (gen !== generation.current) return;
        if (filterRef.current.art === "alle" && ohneZusatz(filterRef.current)) feedMerken(leadId, r);
        if (r.angeheftet) setAngeheftet(r.angeheftet);
        else if ((filterRef.current.art !== "notizen" && filterRef.current.art !== "alle") || !ohneZusatz(filterRef.current)) setAngeheftet([]);
        setEintraege((alt) => {
          if (!zusammenfuehren) return r.data;
          // ältere, schon geladene Einträge behalten
          const neu = new Set(r.data.map((a) => a.id));
          const grenze = r.data.length ? zeitVon(r.data[r.data.length - 1]) : "";
          return [...r.data, ...alt.filter((a) => !neu.has(a.id) && zeitVon(a) < grenze)];
        });
        if (!zusammenfuehren) {
          setCursor(r.cursor);
          setHatMehr(r.has_more);
        }
      } catch (e) {
        if (gen === generation.current) setFehler(e);
      } finally {
        if (gen === generation.current) setLaedt(false);
      }
    },
    [leadId, parameter],
  );

  useEffect(() => {
    const c = filter.art === "alle" && ohneZusatz(filter) ? feedAusCache(leadId) : null;
    setEintraege(c?.data ?? []);
    setAngeheftet(c?.angeheftet ?? []);
    setCursor(c?.cursor ?? null);
    setHatMehr(c?.has_more ?? false);
    setLaedt(!c);
    void laden(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, filter.art, filter.typId, filter.q, filter.userId, filter.contactId, filter.von, filter.bis, laden]);

  const mehr = useCallback(async () => {
    if (!cursor || laedtMehr) return;
    setLaedtMehr(true);
    const gen = generation.current;
    try {
      const r = await leadTiefeApi.verlauf(leadId, { ...parameter(), cursor });
      if (gen !== generation.current) return;
      setEintraege((alt) => {
        const bekannt = new Set(alt.map((a) => a.id));
        return [...alt, ...r.data.filter((a) => !bekannt.has(a.id))];
      });
      setCursor(r.cursor);
      setHatMehr(r.has_more);
    } catch (e) {
      setFehler(e);
    } finally {
      setLaedtMehr(false);
    }
  }, [cursor, laedtMehr, leadId, parameter]);

  const einfuegen = useCallback((a: Aktivitaet) => {
    // eine geänderte angeheftete Notiz bleibt oben angeheftet und aktuell
    setAngeheftet((l) => (l.some((x) => x.id === a.id) ? l.map((x) => (x.id === a.id ? a : x)) : l));
    if (!passt(a, filterRef.current)) return;
    setEintraege((l) => einsortieren(l, a));
  }, []);

  const anheften = useCallback(async (a: Aktivitaet, an: boolean) => {
    const r = await aktivitaetenApi.anheften(a.id, an);
    setEintraege((l) => l.map((x) => (x.id === a.id ? r.activity : x)));
    setAngeheftet((l) => (an ? [r.activity, ...l.filter((x) => x.id !== a.id)] : l.filter((x) => x.id !== a.id)));
  }, []);

  const entfernen = useCallback((id: string) => {
    setEintraege((l) => l.filter((a) => a.id !== id));
    setAngeheftet((l) => l.filter((a) => a.id !== id));
  }, []);

  const aktualisieren = useCallback(
    async (id: string) => {
      if (filterRef.current.q) {
        void laden(false); // bei einer Suche entscheidet der Server, ob der Eintrag noch passt
        return;
      }
      try {
        const r = await aktivitaetenApi.eine(id);
        if (r.activity.lead_id && r.activity.lead_id !== leadId) return;
        if (passt(r.activity, filterRef.current)) setEintraege((l) => einsortieren(l, r.activity));
        else setEintraege((l) => l.filter((a) => a.id !== id));
      } catch (e) {
        if (e instanceof ApiFehler && e.status === 404) setEintraege((l) => l.filter((a) => a.id !== id));
        else void laden(true);
      }
    },
    [leadId, laden],
  );

  return {
    eintraege,
    angeheftet,
    anheften,
    hatMehr,
    laedt,
    laedtMehr,
    fehler,
    mehr: () => void mehr(),
    neu: () => void laden(true),
    einfuegen,
    entfernen,
    aktualisieren: (id) => void aktualisieren(id),
  };
}

// ---------------------------------------------------------------------------
// Darstellung
// ---------------------------------------------------------------------------

interface FeedProps {
  leadId: string;
  feed: FeedDaten;
  filter: FeedFilter;
  setFilter: (f: FeedFilter) => void;
  kontakte: Kontakt[];
  /** `?aktivitaet=<id>`: zu dieser Aktivität blättern und sie kurz hervorheben */
  springeZu?: string | null;
}

export function Feed({ leadId, feed, filter, setFilter, kontakte, springeZu }: FeedProps) {
  const meta = useMeta();
  const ende = useRef<HTMLDivElement>(null);
  const liste = useRef<HTMLOListElement>(null);
  const sucheFeld = useRef<HTMLInputElement>(null);
  const mehrRef = useRef(feed.mehr);
  mehrRef.current = feed.mehr;
  const hatMehrRef = useRef(feed.hatMehr);
  hatMehrRef.current = feed.hatMehr;
  const [logFilter, setLogFilter] = useState<LogFilter & ZeitFilterWert>({ gruppe: null, userId: null, von: null, bis: null });
  const imLog = filter.art === "log";
  const hatAusgehendeSms = feed.eintraege.some((a) => a._type === "SMS" && a.direction !== "inbound" && a.direction !== "incoming");
  const smsZustand = useSmsZustand(leadId, hatAusgehendeSms);

  useEffect(() => {
    const el = ende.current;
    if (!el) return;
    const b = new IntersectionObserver(
      (e) => {
        if (e.some((x) => x.isIntersecting) && hatMehrRef.current) mehrRef.current();
      },
      { rootMargin: "400px" },
    );
    b.observe(el);
    return () => b.disconnect();
  }, [imLog]);

  useEffect(() => setLogFilter({ gruppe: null, userId: null, von: null, bis: null }), [leadId]);

  // Sprung zu einer Aktivität (?aktivitaet=<id>): ist sie geladen, hinscrollen und kurz hervorheben, sonst weiter nachladen
  const gesprungen = useRef<string | null>(null);
  const nachladeVersuche = useRef(0);
  useEffect(() => {
    nachladeVersuche.current = 0;
    gesprungen.current = null;
  }, [springeZu, leadId]);
  useEffect(() => {
    if (!springeZu || gesprungen.current === springeZu || imLog) return;
    const el = liste.current?.querySelector<HTMLElement>(`[data-aktivitaet="${CSS.escape(springeZu)}"]`);
    if (el) {
      gesprungen.current = springeZu;
      el.scrollIntoView({ block: "center" });
      el.classList.add("ls-eintrag--sprung");
      window.setTimeout(() => el.classList.remove("ls-eintrag--sprung"), 3200);
      return;
    }
    if (filter.art !== "alle" || feed.laedt || feed.laedtMehr) return;
    if (feed.hatMehr && nachladeVersuche.current < 40) {
      nachladeVersuche.current += 1;
      feed.mehr();
    }
  }, [springeZu, imLog, feed, filter.art]);

  // ⌘F / Strg+F springt ins Suchfeld des Verlaufs (wie Close); ist es schon aktiv, gilt die Suche des Browsers
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== "f") return;
      const feld = sucheFeld.current;
      if (!feld || document.activeElement === feld) return;
      if (document.querySelector('[role="dialog"], [aria-modal="true"]')) return;
      e.preventDefault();
      feld.focus();
      feld.select();
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, []);

  // Treffer der Suche einfärben (ohne den Text zu verändern)
  const eintragSumme = feed.eintraege.length + feed.angeheftet.length;
  useEffect(() => {
    if (imLog || !filter.q) {
      trefferLoeschen();
      return;
    }
    treffer(liste.current, filter.q);
    return trefferLoeschen;
  }, [filter.q, eintragSumme, imLog, feed.laedt]);

  const angeheftetIds = new Set(feed.angeheftet.map((a) => a.id));
  const sichtbar = ohneDoppelteThreads(feed.angeheftet.length ? feed.eintraege.filter((a) => !angeheftetIds.has(a.id)) : feed.eintraege);
  const typen = (meta.meta?.custom_activity_types ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, "de", { numeric: true }));
  const zusatzAktiv = !ohneZusatz(filter);
  const zuruecksetzen = () => setFilter({ art: filter.art, typId: filter.typId, q: "", userId: null, contactId: null, zeit: null, von: null, bis: null });
  const logZusatz = !!(logFilter.gruppe || logFilter.userId || logFilter.von || logFilter.bis);

  return (
    <section className="ls-feed" aria-label="Aktivitäten">
      <div className="ls-feed__filter">
        <div className="ls-feed__zeile">
          <div className="ls-feed__reiter" role="tablist" aria-label="Aktivitäten filtern">
            {ARTEN.map((a) => (
              <button
                key={a.art}
                type="button"
                role="tab"
                aria-label={a.label}
                aria-selected={filter.art === a.art}
                className={`ls-feed__tab${filter.art === a.art ? " ls-feed__tab--an" : ""}`}
                onClick={() => setFilter({ ...filter, art: a.art, typId: a.art === "custom" ? filter.typId : null })}
              >
                {a.kurz ? (
                  <>
                    <span className="ls-feed__tab-lang">{a.label}</span>
                    <span className="ls-feed__tab-kurz" aria-hidden="true">{a.kurz}</span>
                  </>
                ) : (
                  a.label
                )}
              </button>
            ))}
          </div>
          {filter.art === "custom" ? (
            <Auswahl
              optionen={typen.map((t) => ({ wert: t.id, label: t.name, unter: t.is_archived ? "archiviert" : null }))}
              wert={filter.typId ?? null}
              leerErlaubt
              platzhalter="Alle Typen"
              knopfKlasse="ls-feed__typ"
              breite={280}
              onWahl={(w) => setFilter({ ...filter, art: "custom", typId: (w as string | null) ?? null })}
            />
          ) : null}
        </div>
        <div className="ls-feed__zeile ls-feed__zeile--filter">
          {imLog ? (
            <>
              <Auswahl
                optionen={Object.entries(TL.log.arten)
                  .filter(([k]) => k !== "alle")
                  .map(([wert, label]) => ({ wert, label }))}
                wert={logFilter.gruppe}
                leerErlaubt
                platzhalter={TL.log.arten.alle}
                knopfKlasse={`lf-knopf${logFilter.gruppe ? " lf-knopf--an" : ""}`}
                breite={220}
                titel="Nach Art filtern"
                onWahl={(w) => setLogFilter({ ...logFilter, gruppe: (w as string | null) ?? null })}
              />
              <NutzerFilter wert={logFilter.userId} onChange={(id) => setLogFilter({ ...logFilter, userId: id })} />
              <ZeitFilter wert={logFilter} onChange={(z) => setLogFilter({ ...logFilter, ...z, von: z.von ?? null, bis: z.bis ?? null })} />
              {logZusatz ? (
                <button type="button" className="lf-zurueck" onClick={() => setLogFilter({ gruppe: null, userId: null, von: null, bis: null })}>
                  {TL.filter.zuruecksetzen}
                </button>
              ) : null}
            </>
          ) : (
            <>
              <SucheFeld wert={filter.q ?? ""} onChange={(q) => setFilter({ ...filter, q })} eingabeRef={sucheFeld} zahl={filter.q && !feed.laedt && !feed.fehler && !feed.hatMehr ? feed.eintraege.length : null} />
              <NutzerFilter wert={filter.userId} onChange={(id) => setFilter({ ...filter, userId: id })} />
              <KontaktFilter kontakte={kontakte} wert={filter.contactId} onChange={(id) => setFilter({ ...filter, contactId: id })} />
              <ZeitFilter wert={filter} onChange={(z) => setFilter({ ...filter, ...z })} />
              {zusatzAktiv ? (
                <button type="button" className="lf-zurueck" onClick={zuruecksetzen}>
                  {TL.filter.zuruecksetzen}
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
      {imLog ? (
        <LogReiter leadId={leadId} filter={logFilter} />
      ) : (
        <>
          {feed.fehler ? <FehlerBox fehler={feed.fehler} onNochmal={feed.neu} kompakt /> : null}
          {feed.laedt && !feed.eintraege.length ? <Laden text="Aktivitäten werden geladen …" klein /> : null}
          {!feed.laedt && !feed.fehler && !feed.eintraege.length ? (
            <div className="ls-feed__leer">
              {filter.q ? TL.suche.keineTreffer : filter.art === "alle" && !zusatzAktiv ? T.feed.leer : "Keine Aktivitäten mit diesen Filtern."}
              {filter.art !== "alle" || zusatzAktiv ? (
                <button type="button" className="textknopf" onClick={() => setFilter({ art: "alle" })}>
                  Alle zeigen
                </button>
              ) : null}
            </div>
          ) : null}
          <ol className="ls-feed__liste" ref={liste}>
            {feed.angeheftet.map((a) => (
              <FeedEintrag key={`pin-${a.id}`} a={a} leadId={leadId} kontakte={kontakte} feed={feed} oben smsZustand={smsZustand} />
            ))}
            {feed.angeheftet.length ? <li className="ls-feed__trenner" aria-hidden="true" /> : null}
            {(filter.art === "alle" || filter.art === "emails") && ohneZusatz(filter) ? <MailEntwuerfeImFeed leadId={leadId} kontakte={kontakte} onGesendet={feed.neu} /> : null}
            {sichtbar.map((a, i) => {
              const tag = tagKey(zeitVon(a));
              const davor = i > 0 ? tagKey(zeitVon(sichtbar[i - 1])) : null;
              return (
                <Fragment key={a.id}>
                  {tag && tag !== davor ? (
                    <li className="ls-feed__tag" aria-label={tagLabel(tag)}>
                      <span>{tagLabel(tag)}</span>
                    </li>
                  ) : null}
                  <FeedEintrag a={a} leadId={leadId} kontakte={kontakte} feed={feed} smsZustand={smsZustand} />
                </Fragment>
              );
            })}
          </ol>
          <div ref={ende} className="ls-feed__ende">
            {feed.hatMehr ? (
              <button type="button" className="knopf knopf--klein" disabled={feed.laedtMehr} onClick={feed.mehr}>
                {feed.laedtMehr ? "Lädt …" : T.feed.aelter}
              </button>
            ) : feed.eintraege.length > 10 ? (
              <span>Ende des Verlaufs.</span>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}

function zahlWert(w: unknown): number | null {
  return typeof w === "number" && Number.isFinite(w) && w > 0 ? w : null;
}

/** Wann Close die Aufnahme löscht: die Angabe aus Close, sonst 7 Tage nach dem Anruf */
function aufnahmeAblaufVon(a: Aktivitaet): string | null {
  const roh = a.recording_expires_at;
  if (typeof roh === "string" && roh) return roh;
  const basis = zeitVon(a);
  return basis && !Number.isNaN(new Date(basis).getTime()) ? new Date(new Date(basis).getTime() + 7 * 86_400_000).toISOString() : null;
}

const SYMBOL: Record<string, { icon: IconName; klasse: string }> = {
  Call: { icon: "telefon", klasse: "call" },
  Note: { icon: "notiz", klasse: "note" },
  Email: { icon: "mail", klasse: "email" },
  EmailThread: { icon: "mail", klasse: "email" },
  SMS: { icon: "sms", klasse: "sms" },
  WhatsAppMessage: { icon: "sms", klasse: "sms" },
  Meeting: { icon: "meeting", klasse: "meeting" },
  CustomActivity: { icon: "aktivitaet", klasse: "custom" },
  LeadStatusChange: { icon: "status", klasse: "status" },
  OpportunityStatusChange: { icon: "opportunity", klasse: "status" },
  TaskCompleted: { icon: "erledigt", klasse: "task" },
  Created: { icon: "angelegt", klasse: "status" },
  LeadMerge: { icon: "zusammen", klasse: "status" },
};

function text(a: Aktivitaet, schluessel: string): string | null {
  const w = a[schluessel];
  return typeof w === "string" && w ? w : null;
}

/** Zustand ausgehender SMS (Gesendet, Zugestellt, Fehlgeschlagen) aus dem SMS-Abgleich, je Aktivität. Ohne Antwort bleibt der Stand der Aktivität. */
function useSmsZustand(leadId: string, aktiv: boolean): Record<string, SmsZeile> {
  const [karte, setKarte] = useState<Record<string, SmsZeile>>({});
  useEffect(() => {
    setKarte({});
  }, [leadId]);
  useEffect(() => {
    if (!aktiv) return;
    let vorbei = false;
    smsEingangApi
      .liste({ lead_id: leadId, richtung: "ausgehend", limit: 100 })
      .then((r) => {
        if (vorbei) return;
        const neu: Record<string, SmsZeile> = {};
        for (const z of r.data) if (z.activity_id) neu[z.activity_id] = z;
        setKarte(neu);
      })
      .catch(() => undefined);
    return () => {
      vorbei = true;
    };
  }, [leadId, aktiv]);
  return karte;
}

const SMS_ZUSTAND: Record<string, { label: string; klasse: string }> = {
  geplant: { label: "Geplant", klasse: "" },
  sendet: { label: "Wird gesendet", klasse: "" },
  gesendet: { label: "Gesendet", klasse: "ls-chip--ok" },
  zugestellt: { label: "Zugestellt", klasse: "ls-chip--ok" },
  fehlgeschlagen: { label: "Fehlgeschlagen", klasse: "ls-chip--fehler" },
  abgebrochen: { label: "Abgebrochen", klasse: "" },
};

/** Zustand-Chip an einer ausgehenden SMS; bei Fehlschlag mit dem Fehlertext darunter */
function smsZustandChip(a: Aktivitaet, zeile: SmsZeile | undefined): { chip: React.ReactNode; fehler: string | null } | null {
  let schluessel: string | null = zeile?.status && zeile.status !== "empfangen" ? zeile.status : null;
  let fehler: string | null = zeile?.fehler_text ?? null;
  if (!schluessel) {
    const roh = typeof a.status === "string" ? a.status : "";
    schluessel = roh === "sent" ? "gesendet" : roh === "error" ? "fehlgeschlagen" : roh === "outbox" ? "sendet" : roh === "scheduled" ? "geplant" : null;
    if (schluessel === "fehlgeschlagen") fehler = typeof a.error_message === "string" && a.error_message ? a.error_message : null;
  }
  const z = schluessel ? SMS_ZUSTAND[schluessel] : null;
  if (!z) return null;
  return { chip: <span className={`ls-chip${z.klasse ? ` ${z.klasse}` : ""}`}>{z.label}</span>, fehler: schluessel === "fehlgeschlagen" ? fehler : null };
}

function FeedEintrag({ a, leadId, kontakte, feed, oben = false, smsZustand }: { a: Aktivitaet; leadId: string; kontakte: Kontakt[]; feed: FeedDaten; oben?: boolean; smsZustand?: Record<string, SmsZeile> }) {
  const meta = useMeta();
  const meldung = useMeldung();
  const anrufen = useAnrufen();
  const [bearbeiten, setBearbeiten] = useState(false);
  const symbol = SYMBOL[a._type] ?? { icon: "aktivitaet" as IconName, klasse: "status" };
  const nutzerName = a.user_name ?? (a.user_id ? meta.nutzerName(a.user_id) : null);
  const kontakt = a.contact_id ? kontakte.find((k) => k.id === a.contact_id) : undefined;
  // Close liefert leere Anrufnotizen als „<body><p></p></body>“: nur echter Text zählt
  const callNotiz = a._type === "Call" && !!(String(a.note ?? "").trim() || (a.note_html ?? "").replace(/<[^>]*>|&nbsp;/g, "").trim());
  // Aufnahme (Close-Altbestand, nur solange Close sie noch hat) und Mailbox-Nachricht: mit Player
  // CRM-Telefonie (Twilio) hält die Aufnahme ohne Ablauf; Close-Altbestand läuft nach 7 Tagen ab
  const twilioAufnahme = a._type === "Call" && a.recording_source === "twilio";
  const aufnahmeAblauf = a._type === "Call" && !twilioAufnahme ? aufnahmeAblaufVon(a) : null;
  const hatAufnahme = a._type === "Call" && typeof a.recording_url === "string" && !!a.recording_url && (twilioAufnahme || (!!aufnahmeAblauf && new Date(aufnahmeAblauf).getTime() > Date.now()));
  const hatMailbox = a._type === "Call" && typeof a.voicemail_url === "string" && !!a.voicemail_url;
  const kompakt = ["LeadStatusChange", "OpportunityStatusChange", "TaskCompleted", "Created", "LeadMerge"].includes(a._type) || (a._type === "Call" && !callNotiz && !hatMailbox && !hatAufnahme && !bearbeiten);
  const zeit = zeitVon(a);
  const entwurf = a._type === "CustomActivity" && a.status === "draft";
  const angeheftet = !!a.crm?.pinned;

  // Rückgängig statt Nachfragen (Design-Runde 2): der Eintrag verschwindet sofort, gelöscht wird erst nach der
  // Rückgängig-Frist; ein Klick auf „Rückgängig“ holt ihn zurück, dann wurde nichts gelöscht.
  const loeschen = () => {
    feed.entfernen(a.id);
    const frist = loeschFristStarten(`/api/custom-activities/${encodeURIComponent(a.id)}`, LOESCH_FRIST_MS, () => {
      aktivitaetenApi.customLoeschen(a.id).catch((e) => {
        feed.einfuegen(a);
        meldung.fehler(e, "Nicht gelöscht");
      });
    });
    meldung.zeige(
      "ok",
      "Custom Activity gelöscht.",
      {
        label: "Rückgängig",
        onClick: () => {
          frist.rueckgaengig();
          feed.einfuegen(a);
        },
      },
      LOESCH_FRIST_MS,
    );
  };

  // Notiz löschen: gleiche Rückgängig-Logik wie bei Custom Activities, 5 s Frist, erst danach wird wirklich gelöscht.
  const notizLoeschen = () => {
    setBearbeiten(false);
    feed.entfernen(a.id);
    const frist = loeschFristStarten(`/api/notes/${encodeURIComponent(a.id)}`, NOTIZ_LOESCH_FRIST_MS, () => {
      aktivitaetenApi.notizLoeschen(a.id).catch((e) => {
        feed.einfuegen(a);
        if (a.crm?.pinned) feed.neu();
        meldung.fehler(e, "Notiz nicht gelöscht");
      });
    });
    meldung.zeige(
      "ok",
      "Notiz gelöscht.",
      {
        label: "Rückgängig",
        onClick: () => {
          frist.rueckgaengig();
          feed.einfuegen(a);
          if (a.crm?.pinned) feed.neu(); // die angeheftete Kopie oben kommt vom Server zurück
        },
      },
      NOTIZ_LOESCH_FRIST_MS,
    );
  };

  const angeheftetMarke = oben ? (
    <span className="ls-angeheftet" title="Oben im Verlauf angeheftet">
      <Icon name="stecknadel" groesse={11} />
      Angeheftet
    </span>
  ) : null;
  const pinKnopf = (
    <button
      type="button"
      className={`symbolknopf symbolknopf--klein${angeheftet ? " ls-pin--an" : ""}`}
      title={angeheftet ? "Nicht mehr oben anheften" : "Oben im Verlauf anheften"}
      aria-label={angeheftet ? "Notiz lösen" : "Notiz anheften"}
      aria-pressed={angeheftet}
      onClick={() => void feed.anheften(a, !angeheftet).catch((e) => meldung.fehler(e, "Nicht angeheftet"))}
    >
      <Icon name="stecknadel" groesse={13} />
    </button>
  );
  // Ein Klick in den Notiztext öffnet das Bearbeiten (wie Close); Links, Knöpfe und markierter Text bleiben, wie sie sind
  const klickZumBearbeiten = a.crm?.editable && (a._type === "Note" || a._type === "Call")
    ? (e: React.MouseEvent) => {
        const ziel = e.target as HTMLElement;
        if (ziel.closest("a, button")) return;
        if (window.getSelection()?.toString()) return;
        setBearbeiten(true);
      }
    : undefined;
  const notizBearbeiten = (art: "Note" | "Call") => (
    <NotizBearbeiten
      a={a}
      art={art}
      onGespeichert={(neu) => feed.einfuegen(neu)}
      onFertig={() => setBearbeiten(false)}
      onLoeschen={art === "Note" && a.crm?.deletable ? notizLoeschen : undefined}
    />
  );

  let titel: React.ReactNode = null;
  let zusatz: React.ReactNode = null;
  let inhalt: React.ReactNode = null;
  let aktionen: React.ReactNode = null;

  switch (a._type) {
    case "Call": {
      const ein = a.direction === "inbound" || a.direction === "incoming";
      const nummer = text(a, "phone") ?? text(a, "remote_phone");
      const dispo = a.disposition ? DISPOSITION[a.disposition] ?? a.disposition : null;
      const verpasst = ein && a.disposition && a.disposition !== "answered";
      titel = (
        <>
          {ein ? "Anruf von" : "Anruf an"} <span className="ls-eintrag__wer">{kontakt?.name ?? (nummer ? telefonText(nummer) : "unbekannt")}</span>
          <Icon name={ein ? "eingehend" : "ausgehend"} groesse={12} className={`ls-richtung ls-richtung--${verpasst ? "verpasst" : ein ? "ein" : "aus"}`} />
          {angeheftetMarke}
        </>
      );
      zusatz = (
        <>
          {a.duration ? <span className="ls-chip">{dauerText(a.duration)}</span> : null}
          {dispo ? <span className={`ls-chip${a.disposition === "answered" ? " ls-chip--ok" : ""}`}>{dispo}</span> : null}
        </>
      );
      if (bearbeiten) inhalt = notizBearbeiten("Call");
      else if (callNotiz)
        inhalt = (
          <Einklappbar>
            <div className={`rich${klickZumBearbeiten ? " ls-notiz-klick" : ""}`} onClick={klickZumBearbeiten} dangerouslySetInnerHTML={{ __html: sicheresHtml(a.note_html ?? textZuHtml(String(a.note ?? ""))) }} />
          </Einklappbar>
        );
      if (hatAufnahme || hatMailbox)
        inhalt = (
          <>
            {inhalt}
            {hatAufnahme ? (
              <div className="ls-aufnahme">
                <AufnahmePlayer callId={a.id} dauerS={zahlWert(a.recording_duration) ?? zahlWert(a.duration)} laeuftAbAm={aufnahmeAblauf} />
              </div>
            ) : null}
            {hatMailbox ? (
              <div className="ls-aufnahme">
                <AufnahmePlayer callId={a.id} art="voicemail" dauerS={zahlWert(a.voicemail_duration)} />
              </div>
            ) : null}
          </>
        );
      if (!bearbeiten)
        aktionen = (
          <>
            {callNotiz || angeheftet ? pinKnopf : null}
            {a.crm?.editable ? (
              <button type="button" className="symbolknopf symbolknopf--klein" title={callNotiz ? "Notiz bearbeiten" : "Notiz hinzufügen"} aria-label={callNotiz ? "Notiz bearbeiten" : "Notiz hinzufügen"} onClick={() => setBearbeiten(true)}>
                <Icon name="stift" groesse={13} />
              </button>
            ) : null}
            {nummer ? (
              <button type="button" className="symbolknopf symbolknopf--klein" title={`Zurückrufen: ${telefonText(nummer)}`} aria-label="Zurückrufen" onClick={() => void anrufen(nummer, leadId, a.contact_id, { kontaktName: kontakt?.name })}>
                <Icon name="telefon" groesse={13} />
              </button>
            ) : null}
          </>
        );
      break;
    }
    case "Note": {
      titel = (
        <>
          {a.title ? a.title : "Notiz"}
          {angeheftetMarke}
        </>
      );
      inhalt = bearbeiten ? (
        notizBearbeiten("Note")
      ) : (
        <Einklappbar>
          <div className={`rich${klickZumBearbeiten ? " ls-notiz-klick" : ""}`} onClick={klickZumBearbeiten} dangerouslySetInnerHTML={{ __html: sicheresHtml(markerZuMark(a.note_html ?? textZuHtml(String(a.note ?? "")))) }} />
        </Einklappbar>
      );
      if (!bearbeiten)
        aktionen = (
          <>
            {pinKnopf}
            {a.crm?.editable ? (
              <button type="button" className="symbolknopf symbolknopf--klein" title="Notiz bearbeiten" aria-label="Notiz bearbeiten" onClick={() => setBearbeiten(true)}>
                <Icon name="stift" groesse={13} />
              </button>
            ) : null}
          </>
        );
      break;
    }
    case "CustomActivity": {
      const typ = meta.typ(a.custom_activity_type_id);
      const felder = (typ?.fields ?? []).filter((f) => !istLeer(customWert(a, f.id)));
      titel = typ ? <TypName name={typ.name} /> : "Custom Activity";
      zusatz = entwurf ? <span className="ls-chip ls-chip--entwurf">Entwurf</span> : null;
      inhalt =
        bearbeiten && typ ? (
          <CustomActivityBearbeiten a={a} kontakte={kontakte} onFertig={(neu) => (setBearbeiten(false), neu && feed.einfuegen(neu))} />
        ) : felder.length ? (
          <dl className="ls-werte">
            {felder.map((f) => {
              const w = customWert(a, f.id) as FeldWert;
              const lang = (f.type === "textarea" || f.type === "richtextarea") && typeof w === "string";
              return (
                <div key={f.id} className={`ls-werte__zeile${lang ? " ls-werte__zeile--lang" : ""}`}>
                  <dt>{f.name}</dt>
                  <dd>
                    {lang ? (
                      <Einklappbar zeilen={4}>
                        <div className="rich" dangerouslySetInnerHTML={{ __html: sicheresHtml(/<[a-z]/i.test(w as string) ? (w as string) : textZuHtml(w as string)) }} />
                      </Einklappbar>
                    ) : (
                      feldText(f as FeldDef, w, { nutzerName: meta.nutzerName, kontaktName: (id) => kontakte.find((k) => k.id === id)?.name ?? "Kontakt" })
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        ) : (
          <div className="ls-eintrag__meta">Keine Angaben.</div>
        );
      if (!bearbeiten) {
        aktionen = (
          <>
            {a.crm?.editable && typ ? (
              <button type="button" className={entwurf ? "knopf knopf--klein" : "symbolknopf symbolknopf--klein"} title="Bearbeiten" aria-label="Custom Activity bearbeiten" onClick={() => setBearbeiten(true)}>
                {entwurf ? "Fertigstellen" : <Icon name="stift" groesse={13} />}
              </button>
            ) : null}
            {a.crm?.deletable ? (
              <button type="button" className="symbolknopf symbolknopf--klein" title="Löschen" aria-label="Custom Activity löschen" onClick={loeschen}>
                <Icon name="papierkorb" groesse={13} />
              </button>
            ) : null}
          </>
        );
      }
      break;
    }
    case "Email":
    case "EmailThread": {
      // Titel, Text, Anhänge, Verlauf, Antworten und Weiterleiten: Modul Kommunikation (kommunikation/mailFeed.tsx, Runde 6)
      titel = mailTitel(a);
      zusatz = mailZusatz(a);
      inhalt = <MailInhalt a={a} leadId={leadId} kontakte={kontakte} eintraege={feed.eintraege} neu={feed.neu} />;
      break;
    }
    case "SMS":
    case "WhatsAppMessage": {
      const ein = a.direction === "inbound" || a.direction === "incoming";
      titel = (
        <>
          {a._type === "SMS" ? "SMS" : "WhatsApp"} {ein ? "erhalten" : "gesendet"}
          {kontakt ? <span className="ls-eintrag__wer"> · {kontakt.name}</span> : null}
        </>
      );
      const zustand = !ein && a._type === "SMS" ? smsZustandChip(a, smsZustand?.[a.id]) : null;
      if (zustand) zusatz = zustand.chip;
      inhalt =
        text(a, "text") || zustand?.fehler ? (
          <>
            {text(a, "text") ? (
              <Einklappbar>
                <div className="mehrzeilig">{text(a, "text")}</div>
              </Einklappbar>
            ) : null}
            {zustand?.fehler ? <div className="ls-eintrag__meta ls-eintrag__fehler">{zustand.fehler}</div> : null}
          </>
        ) : null;
      break;
    }
    case "Meeting": {
      titel = <>Meeting: {text(a, "title") ?? "ohne Titel"}</>;
      zusatz = text(a, "status") ? <span className="ls-chip">{text(a, "status")}</span> : null;
      inhalt = (
        <>
          {text(a, "starts_at") ? <div className="ls-eintrag__meta">Beginn {kurzeZeit(text(a, "starts_at"))}</div> : null}
          {text(a, "note") ? (
            <Einklappbar>
              <div className="mehrzeilig">{text(a, "note")}</div>
            </Einklappbar>
          ) : null}
        </>
      );
      break;
    }
    case "LeadStatusChange": {
      const alt = meta.status(text(a, "old_status_id"));
      const neu = meta.status(text(a, "new_status_id"));
      titel = (
        <>
          Status <StatusPille label={a.old_status_label ?? alt?.label ?? "—"} farbe={alt?.color} klein /> <Icon name="pfeil-rechts" groesse={11} /> <StatusPille label={a.new_status_label ?? neu?.label ?? "—"} farbe={neu?.color} klein />
        </>
      );
      break;
    }
    case "OpportunityStatusChange":
      titel = (
        <>
          Opportunity {a.old_status_label ?? "—"} <Icon name="pfeil-rechts" groesse={11} /> {a.new_status_label ?? "—"}
        </>
      );
      break;
    case "TaskCompleted":
      titel = (
        <>
          Follow-up erledigt: <span className="ls-eintrag__wer">{text(a, "task_text") ?? "Aufgabe"}</span>
        </>
      );
      break;
    case "Created":
      titel = <>Lead angelegt{text(a, "source") === "api" ? " (Import)" : ""}</>;
      break;
    case "LeadMerge":
      titel = <>Lead zusammengeführt</>;
      break;
    default:
      titel = <>{String(a._type)}</>;
  }

  return (
    <li
      className={`ls-eintrag ls-eintrag--${symbol.klasse}${oben ? " ls-eintrag--oben" : ""}${kompakt ? " ls-eintrag--kompakt" : ""}${entwurf ? " ls-eintrag--entwurf" : ""}${bearbeiten ? " ls-eintrag--bearbeiten" : ""}`}
      data-aktivitaet={a.id}
    >
      <span className={`ls-symbol ls-symbol--${symbol.klasse}`} aria-hidden="true">
        <Icon name={symbol.icon} groesse={12} />
      </span>
      <div className="ls-eintrag__inhalt">
        <div className="ls-eintrag__kopf">
          <span className="ls-eintrag__titel">{titel}</span>
          {zusatz ? <span className="ls-eintrag__zusatz">{zusatz}</span> : null}
          <span className="ls-eintrag__rechts">
            {aktionen ? <span className="ls-eintrag__aktionen">{aktionen}</span> : null}
            {nutzerName ? (
              <span className="ls-eintrag__nutzer" title={nutzerName}>
                <Avatar name={nutzerName} id={a.user_id} groesse={16} />
                <span className="ls-eintrag__nutzername">{nutzerName}</span>
              </span>
            ) : null}
            <time className="ls-eintrag__zeit" dateTime={zeit} title={`${zeitpunktLang(zeit)} · ${relativ(zeit)}`}>
              {oben ? zeitpunktLang(zeit) : kurzeZeit(zeit)}
            </time>
          </span>
        </div>
        {inhalt ? <div className="ls-eintrag__koerper">{inhalt}</div> : null}
      </div>
    </li>
  );
}

/** Lange Inhalte auf wenige Zeilen kürzen, „Mehr anzeigen“ klappt auf. Misst die echte Höhe (auch bei HTML). */
function Einklappbar({ children, zeilen = 6 }: { children: React.ReactNode; zeilen?: number }) {
  const [auf, setAuf] = useState(false);
  const [lang, setLang] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || auf) return;
    const pruefe = () => setLang(el.scrollHeight > el.clientHeight + 4);
    pruefe();
    const ro = new ResizeObserver(pruefe);
    ro.observe(el);
    return () => ro.disconnect();
  }, [auf, children]);
  return (
    <div className="ls-klapp">
      <div ref={ref} className={`ls-klapp__inhalt${auf ? "" : " ls-klapp__inhalt--zu"}${!auf && lang ? " ls-klapp__inhalt--lang" : ""}`} style={{ ["--ls-zeilen" as string]: zeilen }}>
        {children}
      </div>
      {lang ? (
        <button type="button" className="ls-klapp__knopf" aria-expanded={auf} onClick={() => setAuf((x) => !x)}>
          {auf ? "Weniger" : "Mehr anzeigen"}
        </button>
      ) : null}
    </div>
  );
}

const ERGEBNISSE: [string, string][] = [
  ["answered", "Gespräch"],
  ["vm-answer", "Mailbox, ohne Nachricht"],
  ["vm-left", "Mailbox, Nachricht hinterlassen"],
];

/**
 * Notiz einer Notiz-Aktivität oder eines Anrufs im Verlauf bearbeiten (19.10.2026). Gespeichert wird von selbst: kurz nach dem
 * Tippen, sobald man außerhalb klickt, beim Seitenwechsel und beim Schließen der Seite; „Speichern“ speichert und schließt.
 * Esc schließt ebenfalls und speichert vorher. Eine leere Notiz überschreibt nie eine vorhandene (nur Anrufe dürfen sie leeren).
 */
interface NotizWert {
  html: string;
  titel: string;
  ergebnis: string;
}

function NotizBearbeiten({ a, art, onGespeichert, onFertig, onLoeschen }: { a: Aktivitaet; art: "Note" | "Call"; onGespeichert: (neu: Aktivitaet) => void; onFertig: () => void; onLoeschen?: () => void }) {
  const meldung = useMeldung();
  const bausteine = useTextbausteine("notiz");
  const startWert: NotizWert = { html: a.note_html ?? textZuHtml(String(a.note ?? "")), titel: a.title ?? "", ergebnis: a.disposition ?? "" };
  const [wert, setWert] = useState<NotizWert>(startWert);
  const [stand, setStand] = useState<SpeicherStand>("gespeichert");
  const meldungRef = useRef(meldung);
  meldungRef.current = meldung;
  const gespeichertRef = useRef(onGespeichert);
  gespeichertRef.current = onGespeichert;
  const gespeichertWert = useRef(startWert);
  // Ergebnis (Gespräch, Mailbox …) nur bei angenommenen Anrufen aus der CRM-Telefonie
  const ergebnisWaehlbar = art === "Call" && a.source === "CRM-Telefonie" && ERGEBNISSE.some(([w]) => w === startWert.ergebnis);
  const speicher = useRef<AutoSpeicher<NotizWert> | null>(null);
  if (!speicher.current) {
    speicher.current = new AutoSpeicher<NotizWert>(startWert, {
      gleich: (x, y) => x.html === y.html && x.titel === y.titel && x.ergebnis === y.ergebnis,
      nichtSpeichern: (w) => art === "Note" && !w.html.trim(), // eine leere Notiz überschreibt die vorhandene nie
      speichern: async (w) => {
        const alt = gespeichertWert.current;
        const r =
          art === "Call"
            ? await aktivitaetenApi.anrufNotizAendern(a.id, { note_html: w.html, ...(w.ergebnis !== alt.ergebnis && w.ergebnis ? { disposition: w.ergebnis } : {}) })
            : await aktivitaetenApi.notizAendern(a.id, { note_html: w.html, title: w.titel.trim() || null });
        gespeichertWert.current = w;
        gespeichertRef.current(r.activity);
      },
      beiStand: setStand,
      beiFehler: (e) => meldungRef.current.fehler(e, "Notiz nicht gespeichert"),
    });
  }
  const auto = speicher.current;
  const aendern = (teil: Partial<NotizWert>) => {
    const neu = { ...wert, ...teil };
    setWert(neu);
    auto.setze(neu);
  };

  // Seitenwechsel (Eintrag verschwindet) und Schließen der Seite: Ungespeichertes sofort wegschicken
  useEffect(() => {
    const sichern = () => void auto.jetzt();
    window.addEventListener("pagehide", sichern);
    return () => {
      window.removeEventListener("pagehide", sichern);
      void auto.jetzt();
    };
  }, [auto]);

  const fertig = async () => {
    if (await auto.jetzt()) onFertig();
  };

  return (
    <div
      className="ls-notiz-bearbeiten"
      data-notiz-bearbeiten={art}
      onBlur={(e) => {
        // Klick irgendwo anders auf der Seite: speichern (der Editor bleibt offen)
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) void auto.jetzt();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          void fertig();
        }
      }}
    >
      {art === "Note" ? (
        <input className="eingabe ls-eingabe__titelfeld" placeholder={TL.notiz.titelPlatzhalter} aria-label="Titel der Notiz" maxLength={200} value={wert.titel} onChange={(e) => aendern({ titel: e.target.value })} />
      ) : null}
      <NotizEditor wert={wert.html} onChange={(html) => aendern({ html })} autoFokus bausteine={bausteine} onStrgEnter={() => void fertig()} mindesthoehe={72} />
      <div className="ls-notiz-bearbeiten__fuss">
        <button type="button" className="knopf knopf--primaer knopf--klein" data-notiz-speichern="" disabled={stand === "speichert" || (art === "Note" && !wert.html.trim())} onClick={() => void fertig()}>
          {stand === "speichert" ? "Speichert …" : "Speichern"}
        </button>
        {ergebnisWaehlbar ? (
          <label className="ls-notiz-bearbeiten__ergebnis">
            <span>Ergebnis</span>
            <select className="eingabe" value={ERGEBNISSE.some(([w]) => w === wert.ergebnis) ? wert.ergebnis : "answered"} onChange={(e) => aendern({ ergebnis: e.target.value })}>
              {ERGEBNISSE.map(([w, l]) => (
                <option key={w} value={w}>
                  {l}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <span className="ls-notiz-bearbeiten__stand" data-notizstand={stand} aria-live="polite">
          {stand === "speichert" ? "Speichert …" : stand === "gespeichert" ? "Gespeichert" : stand === "fehler" ? "Nicht gespeichert" : "Wird gleich gespeichert"}
        </span>
        {onLoeschen ? (
          <button
            type="button"
            className="symbolknopf symbolknopf--klein ls-notiz-bearbeiten__loeschen"
            title="Notiz löschen"
            aria-label="Notiz löschen"
            onClick={() => {
              auto.verwerfen();
              onLoeschen();
            }}
          >
            <Icon name="papierkorb" groesse={14} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function CustomActivityBearbeiten({ a, kontakte, onFertig }: { a: Aktivitaet; kontakte: Kontakt[]; onFertig: (neu: Aktivitaet | null) => void }) {
  const meta = useMeta();
  const meldung = useMeldung();
  const typ = meta.typ(a.custom_activity_type_id);
  const [werte, setWerte] = useState<Werte>(() => {
    const w: Werte = {};
    for (const f of typ?.fields ?? []) {
      const x = customWert(a, f.id);
      if (!istLeer(x)) w[f.id] = x as FeldWert;
    }
    return w;
  });
  const [serverFehler, setServerFehler] = useState<unknown>(null);
  const rahmen = useRef<HTMLDivElement>(null);
  // Fokus ins Formular, damit Esc (abbrechen) und ⌘ Enter (speichern) sofort greifen
  useEffect(() => {
    rahmen.current?.querySelector<HTMLElement>(".ls-caf__felder :is(input:not([type=hidden]), textarea, select, button)")?.focus({ preventScroll: true });
  }, []);
  if (!typ) return null;
  return (
    <div
      ref={rahmen}
      className="ls-ca-bearbeiten"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          onFertig(null);
        }
      }}
    >
    <CustomActivityFormular
      typ={typ}
      kontakte={kontakte}
      werte={werte}
      onChange={setWerte}
      bearbeiten
      speichernText={a.status === "draft" && sendetNachAussen(typ) ? "Speichern und senden" : undefined}
      tastenHinweis={`${modTaste} Enter speichert · Esc bricht ab`}
      serverFehler={serverFehler}
      onVerwerfen={() => onFertig(null)}
      onSpeichern={async (status) => {
        setServerFehler(null);
        try {
          const r = await aktivitaetenApi.customAendern(a.id, { status, fields: werteFuerApi(typ, werte, true) });
          meldung.ok(status === "draft" ? "Entwurf gespeichert." : "Gespeichert.");
          onFertig(r.activity);
        } catch (e) {
          if (e instanceof ApiFehler && e.code === "pflichtfelder_fehlen") setServerFehler(e);
          meldung.fehler(e, "Nicht gespeichert");
        }
      }}
    />
    </div>
  );
}
